import React, { useEffect, useState, createRef } from "react";
import { Alert, Button, Form, Input, Select, notification } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { Forms } from "models/form";
import { UserModel } from "models/userCollectionModel";
import { useUpdateAdmin } from "lib/core-react/hooks/private/useAdminUser";
import { IUserStatus } from "types/userCollection";
import { Option } from "antd/es/mentions";
import { useAtom } from "jotai";
import { roleAtom } from "lib/core-react/store/store";
import { RoleCollectionModel } from "models/roleCollectionModel";
import { useGetRole } from "lib/core-react/hooks/private";
import { renderOptionsFromEnum } from "components/Form/forms";

interface IProps {
  setIsShowUpdateModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAdminUser: UserModel;
}

const UpdateAdminUser = ({
  setIsShowUpdateModal,
  selectedAdminUser,
}: IProps) => {
  const { updateAdmin, isLoading, isError } = useUpdateAdmin();
  const { getRole } = useGetRole();
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [{ data: roleData, refetch }] = useAtom(roleAtom);

  const RoleData = roleData && new RoleCollectionModel(roleData);

  // Api Call
  useEffect(() => {
    getRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  const formRef: React.Ref<FormInstance<any>> = createRef();
  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({
      name: selectedAdminUser.name,
      is_active: selectedAdminUser.is_active === 1 ? "1" : "0",
      role_ids: selectedAdminUser?.roles?.data.map((role) =>
        role.id?.toString(),
      ),
    });
  }, [selectedAdminUser, form]);

  //hide error message after 5 sec
  useEffect(() => {
    if (isErrorVisible) {
      setTimeout(() => {
        setIsErrorVisible(false);
      }, 5000);
    }
  }, [isErrorVisible]);

  const onFinish = async (value: IUserStatus) => {
    try {
      await updateAdmin(value, Number(selectedAdminUser.id));
      setIsShowUpdateModal(false);
      notification["success"]({
        message: "Updated Admin User successfully",
      });
    } catch (error: any) {
      if (error?.response?.data?.message) {
        notification["error"]({
          message: error.response.data.message,
        });
      }
    }
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    Forms.formValueChange(form, changedValues, allValues);
  };

  return (
    <Form
      onFinish={onFinish}
      form={form}
      layout="vertical"
      name="Update Admin User"
      onFieldsChange={onValuesChange}
      ref={formRef}
      initialValues={{
        name: selectedAdminUser.name,
        role_ids: selectedAdminUser?.roles?.data.map((role) =>
          role.id?.toString(),
        ),
        is_active: selectedAdminUser.is_active.toString(),
      }}
    >
      <Form.Item
        rules={[
          {
            required: true,
            message: "User name is required",
          },
        ]}
        label="Name"
        name="name"
      >
        <Input placeholder="User name" />
      </Form.Item>

      <Form.Item
        rules={[{ required: true, message: "Role is required" }]}
        name="role_ids"
        label="Role"
      >
        <Select
          mode="multiple"
          loading={isLoading}
          placeholder="Please select the Roles"
        >
          {RoleData?.getRoleIdsAsObjectName() &&
            renderOptionsFromEnum(RoleData?.getRoleIdsAsObject())}
        </Select>
      </Form.Item>

      <Form.Item
        rules={[{ required: true, message: "Status is required" }]}
        label="Status"
        name="is_active"
      >
        <Select placeholder="Select status">
          <Option value="1">Active</Option>
          <Option value="0">Inactive</Option>
        </Select>
      </Form.Item>

      <Form.Item>
        <Button
          loading={isLoading}
          type="primary"
          htmlType="submit"
          style={{ width: "100%" }}
        >
          Submit
        </Button>
      </Form.Item>

      {isErrorVisible && (
        <Alert
          style={{ marginTop: "7px" }}
          message={isError}
          closable
          afterClose={() => setIsErrorVisible(false)}
          type="error"
          showIcon
        />
      )}
    </Form>
  );
};

export default UpdateAdminUser;
